/**
 * useSwitchOrganization — calls the switch-organization Edge Function,
 * then refreshes the auth session so the new app_metadata.org_id flows
 * into the JWT, then invalidates every cross-org-sensitive query so
 * the active-org views re-fetch under the new tenant.
 *
 * UX:
 *   - On success: navigate to /app/dashboard (not reload current URL).
 *     A page that was valid for the prior org may be invisible in the
 *     new one — most obviously a task or room URL whose org no longer
 *     matches the active session, which surfaces as the generic
 *     "Failed to load task" / "Failed to load room" wedge under RLS.
 *     window.location.assign() still triggers a full load, so the
 *     in-memory wipe (Zustand/React Query) is preserved.
 *     Caller can opt out via { skipReload: true } for tests.
 *   - Errors map to friendly userMessage (FORBIDDEN_TARGET_ORG,
 *     RATE_LIMITED, AUDIT_FAILED, etc.).
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'
import { PATHS }                                                from '@/router/paths'

export interface SwitchOrganizationVars {
  targetOrganizationId: string
  /** Skip the page reload (tests). Default false. */
  skipReload?:          boolean
}

export interface SwitchOrganizationResult {
  ok:                boolean
  previous_org_id:   string | null
  new_org_id:        string
  new_role:          string
  no_op?:            boolean
}

export function useSwitchOrganization(): UseMutationResult<SwitchOrganizationResult, AppError, SwitchOrganizationVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<SwitchOrganizationResult, AppError, SwitchOrganizationVars>({
    mutationFn: async (vars): Promise<SwitchOrganizationResult> => {
      const { data, error } = await supabase.functions.invoke<SwitchOrganizationResult>('switch-organization', {
        body: { target_organization_id: vars.targetOrganizationId },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let code   = 'SWITCH_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'The server returned no result.', status: 500,
        })
      }

      // Refresh the auth session so the new app_metadata flows into the JWT.
      // Without this, RLS still gates on the old org_id until the next natural
      // refresh (could be up to an hour with default JWT TTL).
      const { error: refreshErr } = await supabase.auth.refreshSession()
      if (refreshErr) {
        throw new AppError({
          code:        'SESSION_REFRESH_FAILED',
          message:     refreshErr.message,
          userMessage: 'Switched on the server but the local session did not refresh. Sign out and back in.',
          status:      500,
          cause:       refreshErr,
        })
      }

      return data
    },

    onSuccess: (_data, vars) => {
      // Wipe every cached query so the next render fetches under the
      // new org. The simpler alternative is to invalidate keys we
      // know are org-scoped, but the safe move is to clear all.
      queryClient.clear()

      if (!vars.skipReload && typeof window !== 'undefined') {
        // Full navigation — wipes Zustand/in-memory state too, and
        // lands on the dashboard router so the URL is always valid
        // under the new org. No org context bleeds across the switch.
        window.location.assign(PATHS.dashboard)
      }
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'INVALID_ORG_ID':         return 'That organization id is not valid.'
    case 'FORBIDDEN_TARGET_ORG':   return 'You don\'t have access to that organization.'
    case 'LOOKUP_FAILED':          return 'Could not verify your access. Try again.'
    case 'UPDATE_FAILED':          return 'Could not switch organizations. Try again.'
    case 'AUDIT_FAILED':           return 'Switch was rolled back due to an audit-trail error. Try again.'
    case 'RATE_LIMITED':           return 'Too many switches in a row. Pause for a moment.'
    default:                       return detail || 'Could not switch organizations.'
  }
}
